import React, { useEffect, useState, useRef } from 'react';

import {
  ErrorIcon,
  ReadOnlyIcon,
  ResetIcon,
  SuccessIcon,
  WarningIcon,
} from './components';
import styles from './TextArea.module.scss';

export interface TextAreaProps
  extends React.DetailedHTMLProps<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    HTMLTextAreaElement
  > {
  description?: string;
  success?: boolean | string;
  warning?: boolean | string;
  error?: boolean | string;
  readOnly?: boolean;
  onReset?: () => void;
}

export const TextArea = ({
  description,
  success,
  warning,
  error,
  readOnly,
  onReset,
  ...props
}: TextAreaProps) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [inputValue, setInputValue] =
    useState<React.TextareaHTMLAttributes<HTMLTextAreaElement>['value']>('');

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    props.onChange?.(e);
  };

  const onResetHandler = () => {
    setInputValue('');
    onReset?.();
  };

  useEffect(() => {
    if (props.value) {
      setInputValue(props.value);
    } else {
      setInputValue('');
    }
  }, [props.value]);

  useEffect(() => {
    if (ref.current) {
      const target = ref.current;
      target.style.height = 'auto';
      target.style.height = `${target.scrollHeight}px`;
    }
  }, [inputValue]);

  let statusIconElement: React.ReactNode = null;
  if (success) statusIconElement = <SuccessIcon />;
  if (warning) statusIconElement = <WarningIcon />;
  if (error) statusIconElement = <ErrorIcon />;

  let suffixIconElement: React.ReactNode = null;
  if (readOnly) suffixIconElement = <ReadOnlyIcon />;
  if (!readOnly && onReset && (props.value || inputValue)) {
    suffixIconElement = (
      <ResetIcon
        onClick={onResetHandler}
        style={{ cursor: 'pointer' }}
        data-test-id="text-area-reset-icon"
      />
    );
  }

  let descriptionElement: string | undefined;
  if (typeof success === 'string') descriptionElement = success;
  else if (typeof warning === 'string') descriptionElement = warning;
  else if (typeof error === 'string') descriptionElement = error;
  else if (description) descriptionElement = description;

  return (
    <div
      className={styles.textArea}
      data-test-id="text-area-container"
      data-disabled={props.disabled}
      data-read-only={readOnly}
      data-has-placeholder={!!props.placeholder}
      data-success={!!success}
      data-warning={!!warning}
      data-error={!!error}
    >
      <div className={styles.inputContainer}>
        <textarea
          {...props}
          ref={ref}
          data-test-id="text-area-input"
          value={props.value || inputValue}
          placeholder={props.placeholder}
          onChange={onChange}
        />
        {statusIconElement && (
          <span
            className={styles.statusIcon}
            data-test-id="text-area-status-icon"
          >
            {statusIconElement}
          </span>
        )}
        {suffixIconElement && (
          <span
            className={styles.suffixIcon}
            data-test-id="text-area-suffix-icon"
          >
            {suffixIconElement}
          </span>
        )}
        {props.placeholder && (
          <span className={styles.label} data-test-id="text-area-label">
            {props.placeholder}
          </span>
        )}
      </div>
      {descriptionElement && (
        <span
          className={styles.description}
          data-test-id="text-area-description"
        >
          {descriptionElement}
        </span>
      )}
    </div>
  );
};
